import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TypesUtils } from "../../../types";
import { GostUtils } from "../cards/recordings/gost-utils";
import { Subject } from "rxjs";
import { DatabaseCacheService } from "../../../data/cache/database-cache.service";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { GostDigestComponent } from "../cards/recordings/gost-digest.component";
import { GOST_SUMMARY_I18N } from "./gost-summary.i18n";
import { DetailsHeaderComponent } from "../measured-data-details/components/shared/details-header/details-header.component";

@Component({
  selector: "gost-summary",
  templateUrl: "./gost-summary.component.html",
  styleUrls: ["./gost-summary.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GostUtils],
  imports: [...SHARED_IMPORTS, GostDigestComponent, DetailsHeaderComponent],
})
export class GostSummaryComponent implements OnInit {
  protected readonly i18n = GOST_SUMMARY_I18N;
  readonly headerTitle = `GOST｜${$localize`:@@home.gostWorkspace.tab.overview:レポート`}`;
  readonly headerBadgeText = $localize`:@@home.gostWorkspace.tab.overview:レポート`;
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  readonly common = inject(DatabaseCacheService);
  private readonly gostUtils = inject(GostUtils);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly measurerLabel = $localize`:@@home.gostMatrix.label.measurer:計測者`;

  userId: string;
  gostRecord: any = null;
  gostRecords: any[] = [];
  selectedRecordIndex = 0;
  gostRecordLengthZero = false;
  recordDataUpdated: Subject<any> = new Subject();
  readonly isLoading = signal(true);

  private latestLoadRequestId = 0;
  private requestedRecordId: string | null = null;

  getHeaderPatientName(): string {
    return this.gostRecord?.data?.userName ?? "";
  }

  getHeaderMeasureDate(): string {
    const d = this.gostRecord?.data;
    const createdAt = d?.createdAt ?? d?.recordedAt ?? "";
    const offsetMatch = createdAt.match(/([+-])(\d{2}):(\d{2})$/);
    const hasNonZeroOffset =
      offsetMatch &&
      !(offsetMatch[1] === "+" && offsetMatch[2] === "00" && offsetMatch[3] === "00");

    if (hasNonZeroOffset && d?.createdAtLocationStr) {
      const dateStr = d.createdAtLocationStr.replace(/[+-]\d{2}:\d{2}$/, "").trim();
      if (!dateStr) return "";
      const h = parseInt(offsetMatch[2], 10);
      const m = parseInt(offsetMatch[3], 10);
      const tzLabel = m > 0
        ? `UTC${offsetMatch[1]}${h}:${String(m).padStart(2, "0")}`
        : `UTC${offsetMatch[1]}${h}`;
      return `${dateStr} (${tzLabel})`;
    }

    const raw = d?.createdAtLocalStr ?? d?.recordedAt ?? createdAt;
    const dateStr = raw.replace(/[+-]\d{2}:\d{2}$/, "").trim();
    if (!dateStr) return "";
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return tz ? `${dateStr} (${tz})` : dateStr;
    } catch {
      return dateStr;
    }
  }

  getHeaderMeasurer(): string {
    const d = this.gostRecord?.data;
    if (d?.recorderName) {
      return `${this.measurerLabel}: ${d.recorderName}`;
    }
    return "";
  }

  ngOnInit() {
    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const gostsList = this.common.getUserGostsList();

        if (gostsList == null) {
          this.gostRecords = [];
          this.selectedRecordIndex = 0;
          this.isLoading.set(true);
          this.cdr.markForCheck();
          return;
        }

        const result = TypesUtils.convertJsonToRecords(gostsList, true);
        const records = result.records;

        if (records.length === 0) {
          this.gostRecords = [];
          this.selectedRecordIndex = 0;
          this.gostRecord = null;
          this.gostRecordLengthZero = true;
          this.isLoading.set(false);
          this.cdr.markForCheck();
          return;
        }

        this.gostRecordLengthZero = false;
        this.gostRecords = records;
        const currentId = this.gostRecord?.id;
        const requestedIndex = this.requestedRecordId
          ? this.gostRecords.findIndex((record) => record?.id === this.requestedRecordId)
          : -1;
        const currentIndex = currentId
          ? this.gostRecords.findIndex((record) => record?.id === currentId)
          : -1;
        const targetIndex =
          requestedIndex >= 0
            ? requestedIndex
            : currentIndex >= 0
              ? currentIndex
              : 0;

        void this.selectRecordByIndex(targetIndex);
    });

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    this.route.params
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(async (params) => {

      const userId = params["userId"];
      this.userId = userId;
      this.latestLoadRequestId++;
      this.gostRecords = [];
      this.selectedRecordIndex = 0;
      this.isLoading.set(true);
      this.gostRecord = null;
      this.gostRecordLengthZero = false;
      this.cdr.markForCheck();
      await this.common.setSelectedUserId(this.userId);
      this.common.prepareUserGostList(userId, false);
    });

    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((queryParams) => {
        this.requestedRecordId = queryParams.get("recordId");
        if (!this.requestedRecordId || this.gostRecords.length === 0) {
          return;
        }
        const targetIndex = this.gostRecords.findIndex(
          (record) => record?.id === this.requestedRecordId,
        );
        if (targetIndex >= 0 && targetIndex !== this.selectedRecordIndex) {
          void this.selectRecordByIndex(targetIndex);
        }
      });
  }

  navigateToNewGostPage() {
    this.router.navigate([
      "/home/",
      this.common.getSelectedSection().sectionId,
      "users",
      this.common.getSelectedUserId(),
      "gost",
      "manual",
    ]);
  }

  canNavigateOlder(): boolean {
    return !this.isLoading()
      && this.selectedRecordIndex >= 0
      && this.selectedRecordIndex < this.gostRecords.length - 1;
  }

  canNavigateNewer(): boolean {
    return !this.isLoading()
      && this.selectedRecordIndex > 0
      && this.selectedRecordIndex < this.gostRecords.length;
  }

  showOlderRecord() {
    if (!this.canNavigateOlder()) {
      return;
    }
    void this.selectRecordByIndex(this.selectedRecordIndex + 1);
  }

  showNewerRecord() {
    if (!this.canNavigateNewer()) {
      return;
    }
    void this.selectRecordByIndex(this.selectedRecordIndex - 1);
  }

  private hasDigestPayload(record: any): boolean {
    const data = record?.data;
    return !!data
      && Array.isArray(data?.PrimarilySelectedFoods?.selectedFoods)
      && Array.isArray(data?.SecondarySelectedFoods)
      && data.SecondarySelectedFoods.length >= 8
      && data?.gostSCData != null
      && data?.gostGCData != null
      && data?.gostOCData != null;
  }

  private updateDisplayedRecord(record: any): void {
    this.gostRecord = record;
    try {
      this.gostUtils.evaluateGost(this.gostRecord);
    } catch (error) {
      console.warn(
        "[WARN] gost-summary.component.ts > evaluateGost failed, rendering raw record",
        error,
      );
    }
    this.recordDataUpdated.next(this.gostRecord);
  }

  private async selectRecordByIndex(index: number): Promise<void> {
    if (index < 0 || index >= this.gostRecords.length) {
      return;
    }

    const listRecord = this.gostRecords[index];
    if (listRecord == null) {
      return;
    }

    this.selectedRecordIndex = index;
    const requestId = ++this.latestLoadRequestId;

    if (this.hasDigestPayload(listRecord)) {
      this.updateDisplayedRecord(listRecord);
      this.isLoading.set(false);
      this.cdr.markForCheck();
      return;
    }

    this.isLoading.set(true);
    this.cdr.markForCheck();
    try {
      const loadedRecord = await this.common.getGostRecord(listRecord.id);
      if (requestId !== this.latestLoadRequestId) {
        return;
      }
      this.gostRecords[index] = loadedRecord;
      this.updateDisplayedRecord(loadedRecord);
    } catch (error) {
      if (requestId !== this.latestLoadRequestId) {
        return;
      }
      this.updateDisplayedRecord(listRecord);
      console.error(
        "[ERROR] gost-summary.component.ts > load selected GOST record | error",
        error,
      );
    } finally {
      if (requestId === this.latestLoadRequestId) {
        this.isLoading.set(false);
        this.cdr.markForCheck();
      }
    }
  }
}
